import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { adminApi, type TeamMember } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { getTeamIcon, TEAM_ICON_OPTIONS } from '@/lib/team-icons';

const ICON_LABEL: Record<string, string> = {
  crown: 'Корона',
  shield: 'Щит',
  wrench: 'Ключ',
  sparkles: 'Искры',
  heart: 'Сердце',
  star: 'Звезда',
  user: 'Силуэт',
};

type Form = {
  id?: number;
  name: string;
  role: string;
  icon: string;
  color: string;
  description: string;
  avatarUrl: string;
  sortOrder: number;
  isVisible: boolean;
};

const emptyForm = (): Form => ({
  name: '',
  role: '',
  icon: 'shield',
  color: '#FF95CE',
  description: '',
  avatarUrl: '',
  sortOrder: 0,
  isVisible: true,
});

function toPayload(f: Form): Partial<TeamMember> {
  return {
    name: f.name,
    role: f.role,
    icon: f.icon,
    color: f.color,
    description: f.description || null,
    avatarUrl: f.avatarUrl || null,
    sortOrder: f.sortOrder,
    isVisible: f.isVisible,
  };
}

export function AdminTeam() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'team'], queryFn: adminApi.team.list });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(emptyForm());

  const saveMut = useMutation({
    mutationFn: () =>
      form.id ? adminApi.team.update(form.id, toPayload(form)) : adminApi.team.create(toPayload(form)),
    onSuccess: () => {
      toast.success('Сохранено');
      qc.invalidateQueries({ queryKey: ['admin', 'team'] });
      qc.invalidateQueries({ queryKey: ['team'] });
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => adminApi.team.remove(id),
    onSuccess: () => {
      toast.success('Удалено');
      qc.invalidateQueries({ queryKey: ['admin', 'team'] });
      qc.invalidateQueries({ queryKey: ['team'] });
    },
    onError: () => toast.error('Не удалось удалить'),
  });

  function openCreate() {
    setForm(emptyForm());
    setOpen(true);
  }

  function openEdit(m: TeamMember) {
    setForm({
      id: m.id,
      name: m.name ?? '',
      role: m.role ?? '',
      icon: m.icon ?? 'shield',
      color: m.color ?? '#FF95CE',
      description: m.description ?? '',
      avatarUrl: m.avatarUrl ?? '',
      sortOrder: m.sortOrder ?? 0,
      isVisible: m.isVisible ?? true,
    });
    setOpen(true);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-eol-white">Команда сервера</h1>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" /> Добавить участника
        </Button>
      </div>

      {!data?.length ? (
        <p className="text-eol-desc">Команда пока пуста. Добавьте первого участника.</p>
      ) : (
        <div className="space-y-3">
          {data.map((m) => {
            const Icon = getTeamIcon(m.icon);
            return (
              <Card key={m.id}>
                <CardContent className="pt-4 pb-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    {m.avatarUrl ? (
                      <img src={m.avatarUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-eol-pink/30 to-transparent grid place-items-center">
                        <Icon className="h-5 w-5" style={{ color: m.color }} />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-eol-white">{m.name}</p>
                      <Badge variant="secondary" className="mt-1">
                        {m.role}
                      </Badge>
                      {!m.isVisible && (
                        <Badge variant="danger" className="ml-2">
                          Скрыт
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="secondary" onClick={() => openEdit(m)} title="Редактировать">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => {
                        if (confirm(`Удалить «${m.name}»?`)) removeMut.mutate(m.id);
                      }}
                      title="Удалить"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Редактировать участника' : 'Новый участник'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Имя">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Должность / роль">
              <Input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="Владелец / Админ / Модератор…"
              />
            </Field>

            <Field label="Иконка">
              <select
                className="h-10 rounded-md border border-white/10 bg-eol-black/40 px-3 text-sm text-eol-white w-full"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
              >
                {TEAM_ICON_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {ICON_LABEL[k] ?? k}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Цвет иконки">
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="h-10 w-12 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <Input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </Field>

            <Field label="URL аватарки (опционально, перекроет иконку)" className="col-span-2">
              <Input
                value={form.avatarUrl}
                onChange={(e) => setForm({ ...form, avatarUrl: e.target.value })}
                placeholder="https://… или /uploads/me.png"
              />
            </Field>

            <Field label="Краткое описание (опционально)" className="col-span-2">
              <textarea
                rows={2}
                className="w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm text-eol-white"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>

            <Field label="Порядок сортировки">
              <Input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
              />
            </Field>
            <Field label="Видимость">
              <label className="flex items-center gap-2 h-10 text-eol-white">
                <input
                  type="checkbox"
                  checked={form.isVisible}
                  onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
                />
                Показывать на странице
              </label>
            </Field>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1.5 ${className ?? ''}`}>
      <span className="text-xs uppercase font-semibold tracking-wider text-eol-desc">{label}</span>
      {children}
    </label>
  );
}
